import { PureAbility } from '@casl/ability';
import { Observable, throwError } from 'rxjs';
import { catchError, delay, retryWhen, take, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AbilityRule } from '../types';

@Injectable({
  providedIn: 'root',
})
export class SnAbilityService {
  constructor(private readonly _httpClient: HttpClient, private readonly _ability: PureAbility) {}

  public loadPermissions(): Observable<AbilityRule[]> {
    const tryThisMany = 3;
    return this._httpClient.get<AbilityRule[]>('/api/auth/current-permissions/casl/').pipe(
      catchError((err) => {
        return throwError(err);
      }),
      retryWhen((err) => {
        return err.pipe(delay(3000), take(tryThisMany));
      }),
      tap((permissions) => this._ability.update(permissions.map(this._transformActionsToAction))),
    );
  }

  public can(...permissions: AbilityRule[]): boolean {
    return permissions.reduce((acc, permission) => acc && this._can(permission), true);
  }

  public cannot(...permissions: AbilityRule[]): boolean {
    return permissions.reduce((acc, permission) => acc && this._cannot(permission), true);
  }

  public destroyPermissions(): void {
    this._ability.update([]);
  }

  private _can(permissions: AbilityRule): boolean {
    if (Array.isArray(permissions.action)) {
      return permissions.action.reduce(
        (acc, action) => acc && this._ability.can(action, permissions.subject),
        true,
      );
    }

    return this._ability.can(permissions.action, permissions.subject);
  }

  private _cannot(permissions: AbilityRule): boolean {
    if (Array.isArray(permissions.action)) {
      return permissions.action.reduce(
        (acc, action) => acc && this._ability.cannot(action, permissions.subject),
        true,
      );
    }

    return this._ability.cannot(permissions.action, permissions.subject);
  }

  // TODO Remove this transformation function after the BE current-permissions endpoint is updated to return 'action' instead of 'actions' https://taserintl.atlassian.net/browse/FWX-396
  private _transformActionsToAction(ability): AbilityRule {
    if ('action' in ability) {
      return ability;
    }
    return { subject: ability.subject, action: ability.actions };
  }
}
